import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Collapse,
  Grid,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import UpIcon from '@material-ui/icons/KeyboardArrowUp';
import { FormattedMessage, useIntl } from 'react-intl';
import Switch from 'react-switch';
import moment from 'moment';
import Security from '../../../commons/security/Security.js';
import { StyledTableCellProps as StyledTableCell, StyledTooltip, rowsPerPage } from '../../../commons/utils/TableStyles';
import * as Utils from '../../../commons/utils/Utils';
import SortAsc from "../../../assets/images/llantas/table/sort-asc.svg";
import SortDesc from "../../../assets/images/llantas/table/sort-desc.svg";
import Sort from "../../../assets/images/llantas/table/sort.svg";
import LimpiarFiltros from '../../../assets/images/limpiar-filtros.svg';

const STORAGE_KEY = 'BipeosGridFilter';

const PAGINATION_INIT = { page: 0, count: 0, perPage: 10 };

const FILTER_STATE_INIT = {
  dominio: [],
  fechaHoraBipeoDesde: '',
  fechaHoraBipeoHasta: '',
  fechaRecepcionDesde: '',
  fechaRecepcionHasta: '',
  estado: '',
};

const SORT_QUERY_INIT = {
  dominio: "",
  cantidadRutas: "",
  fechaHoraBipeo: "desc",
  fechaRecepcion: "",
  estado: "",
};

var getStoredFilters = function() {
  try {
    var storage = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    if (storage && storage.activo && storage.filtros) return storage.filtros;
  } catch (e) {}
  return JSON.parse(JSON.stringify(FILTER_STATE_INIT));
};

var getStoredPersiste = function() {
  try {
    var storage = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    return !!(storage && storage.activo);
  } catch (e) {}
  return false;
};

const BipeosGrid = function() {
  var intl = useIntl();
  var [loading, setLoading] = useState(false);
  var [exporting, setExporting] = useState(false);
  var [pagination, setPagination] = useState(JSON.parse(JSON.stringify(PAGINATION_INIT)));
  var [paginationCount, setPaginationCount] = useState(0);
  var [filtersState, setFiltersState] = useState(getStoredFilters);
  var [appliedFilterQuery, setAppliedFilterQuery] = useState(getStoredFilters);
  var [sortQuery, setSortQuery] = useState(JSON.parse(JSON.stringify(SORT_QUERY_INIT)));
  var [dropdownActive, setDropdownActive] = useState(false);
  var [bipeos, setBipeos] = useState([]);
  var [dominios, setDominios] = useState([]);
  var [persisteFiltros, setPersisteFiltros] = useState(getStoredPersiste);
  var [ultimaRecepcion, setUltimaRecepcion] = useState(null);
  var [limiteDias, setLimiteDias] = useState(120);

  useEffect(function() {
    Utils.getData('/disponibilidad/select-byDominio')
      .then(function(data) { setDominios(data || []); })
      .catch(function() {});
  }, []);

  useEffect(function() {
    if (!Security.hasAnyPermission(["DISPONIBILIDAD_LISTAR", "DISPONIBILIDAD_ACTIVA_LISTAR"])) return;
    setLoading(true);
    fetchBipeos();
    return function() {};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.perPage, appliedFilterQuery, sortQuery]);

  var buildQuery = function(page, perPage, fq) {
    var f = fq || appliedFilterQuery;
    var q = "?page=" + page;
    q += "&perPage=" + perPage;
    q += "&dominio=" + (f.dominio ? f.dominio.map(function(e) { return e.value; }).join(',') : '');
    q += "&fechaHoraBipeoDesde=" + (f.fechaHoraBipeoDesde || '');
    q += "&fechaHoraBipeoHasta=" + (f.fechaHoraBipeoHasta || '');
    q += "&fechaRecepcionDesde=" + (f.fechaRecepcionDesde || '');
    q += "&fechaRecepcionHasta=" + (f.fechaRecepcionHasta || '');
    q += "&estado=" + (f.estado || '');
    q += "&dominioSort=" + (sortQuery.dominio || '');
    q += "&cantidadRutasSort=" + (sortQuery.cantidadRutas || '');
    q += "&fechaHoraBipeoSort=" + (sortQuery.fechaHoraBipeo || '');
    q += "&fechaRecepcionSort=" + (sortQuery.fechaRecepcion || '');
    q += "&estadoSort=" + (sortQuery.estado || '');
    return q;
  };

  var fetchBipeos = function() {
    Utils.getData('/disponibilidad/bipeos' + buildQuery(pagination.page, pagination.perPage))
      .then(function(data) {
        setBipeos(data.bipeos || []);
        setPaginationCount(data.pagination ? data.pagination.count : 0);
        if (data.ultimaRecepcion) setUltimaRecepcion(data.ultimaRecepcion);
        if (data.limiteDias) setLimiteDias(data.limiteDias);
        setLoading(false);
      })
      .catch(function() { setLoading(false); });
  };

  var handleChange = function(name, value) {
    setFiltersState(function(prev) {
      var updated = Object.assign({}, prev, { [name]: value });
      if (persisteFiltros) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ activo: true, filtros: updated }));
      }
      return updated;
    });
  };

  var handleSubmit = function(e) {
    if (e) e.preventDefault();
    var snapshot = Object.assign({}, filtersState);
    setAppliedFilterQuery(snapshot);
    setPagination(function(p) { return Object.assign({}, p, { page: 0 }); });
  };

  var handleFilterReset = function() {
    var init = JSON.parse(JSON.stringify(FILTER_STATE_INIT));
    setFiltersState(init);
    setAppliedFilterQuery(init);
    setPagination(JSON.parse(JSON.stringify(PAGINATION_INIT)));
    setSortQuery(JSON.parse(JSON.stringify(SORT_QUERY_INIT)));
    setPersisteFiltros(false);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ activo: false, filtros: init }));
    setDropdownActive(false);
  };

  var handlePersisteFiltros = function(value) {
    setPersisteFiltros(value);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ activo: value, filtros: filtersState }));
  };

  var requestSort = function(key) {
    var query = { dominio: "", cantidadRutas: "", fechaHoraBipeo: "", fechaRecepcion: "", estado: "" };
    var prev = sortQuery[key];
    query[key] = !prev || prev === "desc" ? "asc" : "desc";
    setSortQuery(query);
    setPagination(function(p) { return Object.assign({}, p, { page: 0 }); });
  };

  var getSortImg = function(key) {
    if (!sortQuery[key]) return Sort;
    return sortQuery[key] === "asc" ? SortAsc : SortDesc;
  };

  var handleExport = function() {
    setExporting(true);
    var payload = JSON.stringify({ queryFilters: JSON.stringify(appliedFilterQuery) });
    Utils.dataBlob('/disponibilidad/bipeos/exportar-excel', 'POST', payload)
      .then(function(res) {
        var fileUrl = URL.createObjectURL(res.data);
        var link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', res.filename.replace(/"/g, ''));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setExporting(false);
      })
      .catch(function() { setExporting(false); });
  };

  var hoy = moment().format('YYYY-MM-DD');
  var minFecha = moment().subtract(limiteDias, 'days').format('YYYY-MM-DD');

  return (
    <div className="content-wrapper">
      <div className="content-header row">
        <div className="content-header-left col-md-6 col-12 mb-2">
          <h3 className="content-header-title">
            <FormattedMessage id="bipeosGrid.titulo" defaultMessage="Bipeos" />
          </h3>
        </div>
      </div>
      <div className="content-body">
        <div className="card">
          <div className="card-content collpase show">
            <div className="card-body card-dashboard">

              <div className="container-fluid mb-1">
                <div className="row dt-icons">
                  <div className="col">
                    <StyledTooltip
                      title={intl.formatMessage({ id: 'bipeosGrid.button_filtros.title', defaultMessage: 'Filtros' })}
                      arrow
                      placement="right"
                    >
                      <div
                        className="btn btn-grey-blue box-shadow-2 btn-round btn-lg btn-dt-main round-icon"
                        onClick={function() { setDropdownActive(function(d) { return !d; }); }}
                      >
                        {dropdownActive ? <UpIcon /> : <ExpandMoreIcon />}
                      </div>
                    </StyledTooltip>
                  </div>
                  <div className="col" id="buttons">
                    <div className="dt-buttons btn-group">
                      <StyledTooltip
                        title={intl.formatMessage({ id: 'bipeosGrid.button_limpiar.title', defaultMessage: 'Restaurar filtros y ordenamiento' })}
                        arrow
                        placement="top"
                      >
                        <button onClick={handleFilterReset} className="btn btn-secondary buttons-collection" type="button">
                          <span>
                            <img id="filterReset" className="filter-reset" src={LimpiarFiltros} alt="limpiar" />
                          </span>
                        </button>
                      </StyledTooltip>
                      <StyledTooltip
                        title={intl.formatMessage({ id: 'bipeosGrid.button_exportar.title', defaultMessage: 'Exportar a Excel' })}
                        arrow
                        placement="top"
                      >
                        <button
                          className="btn buttons-collection"
                          onClick={handleExport}
                          disabled={exporting || loading}
                          type="button"
                        >
                          <span>
                            {exporting ? <CircularProgress size={14} color="inherit" /> : <i className="ft-upload"></i>}
                          </span>
                        </button>
                      </StyledTooltip>
                    </div>
                  </div>
                </div>
              </div>

              <Collapse in={dropdownActive}>
                <Grid container item xs={12} spacing={3} style={{ marginBottom: 20 }}>

                  <Grid component={Box} item xs={3}>
                    <form noValidate autoComplete="off" onSubmit={handleSubmit}>
                      <Autocomplete
                        multiple
                        size="medium"
                        options={dominios}
                        getOptionLabel={function(o) { return o.label || ''; }}
                        value={filtersState.dominio}
                        defaultValue={[]}
                        onChange={function(e, val) { handleChange('dominio', val); }}
                        renderInput={function(params) {
                          return (
                            <TextField
                              {...params}
                              variant="standard"
                              label={intl.formatMessage({ id: 'bipeosGrid.filtro_dominio', defaultMessage: 'Dominio' })}
                              placeholder={intl.formatMessage({ id: 'bipeosGrid.filtro_dominio', defaultMessage: 'Dominio' })}
                            />
                          );
                        }}
                      />
                    </form>
                  </Grid>

                  <Grid component={Box} item xs={2}>
                    <form noValidate autoComplete="off" onSubmit={handleSubmit}>
                      <TextField
                        label={intl.formatMessage({ id: 'bipeosGrid.filtro_bipeo_desde', defaultMessage: 'Bipeo desde' })}
                        name="fechaHoraBipeoDesde"
                        value={filtersState.fechaHoraBipeoDesde}
                        onChange={function(e) { handleChange('fechaHoraBipeoDesde', e.target.value); }}
                        className="col-12"
                        type="date"
                        format="DD-MM-YYYY"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: minFecha, max: hoy }}
                      />
                    </form>
                  </Grid>

                  <Grid component={Box} item xs={2}>
                    <form noValidate autoComplete="off" onSubmit={handleSubmit}>
                      <TextField
                        label={intl.formatMessage({ id: 'bipeosGrid.filtro_bipeo_hasta', defaultMessage: 'Bipeo hasta' })}
                        name="fechaHoraBipeoHasta"
                        value={filtersState.fechaHoraBipeoHasta}
                        onChange={function(e) { handleChange('fechaHoraBipeoHasta', e.target.value); }}
                        className="col-12"
                        type="date"
                        format="DD-MM-YYYY"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: minFecha, max: hoy }}
                      />
                    </form>
                  </Grid>

                  <Grid component={Box} item xs={2}>
                    <form noValidate autoComplete="off" onSubmit={handleSubmit}>
                      <TextField
                        label={intl.formatMessage({ id: 'bipeosGrid.filtro_recepcion_desde', defaultMessage: 'Recepción desde' })}
                        name="fechaRecepcionDesde"
                        value={filtersState.fechaRecepcionDesde}
                        onChange={function(e) { handleChange('fechaRecepcionDesde', e.target.value); }}
                        className="col-12"
                        type="date"
                        format="DD-MM-YYYY"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: minFecha, max: hoy }}
                      />
                    </form>
                  </Grid>

                  <Grid component={Box} item xs={2}>
                    <form noValidate autoComplete="off" onSubmit={handleSubmit}>
                      <TextField
                        label={intl.formatMessage({ id: 'bipeosGrid.filtro_recepcion_hasta', defaultMessage: 'Recepción hasta' })}
                        name="fechaRecepcionHasta"
                        value={filtersState.fechaRecepcionHasta}
                        onChange={function(e) { handleChange('fechaRecepcionHasta', e.target.value); }}
                        className="col-12"
                        type="date"
                        format="DD-MM-YYYY"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: minFecha, max: hoy }}
                      />
                    </form>
                  </Grid>

                  <Grid component={Box} item xs={2}>
                    <form noValidate autoComplete="off" onSubmit={handleSubmit}>
                      <TextField
                        select
                        label={intl.formatMessage({ id: 'bipeosGrid.filtro_estado', defaultMessage: 'Estado' })}
                        name="estado"
                        value={filtersState.estado}
                        onChange={function(e) { handleChange('estado', e.target.value); }}
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        variant="standard"
                        className="col-12"
                      >
                        <option value="">{intl.formatMessage({ id: 'bipeosGrid.estado_todos', defaultMessage: 'Todos' })}</option>
                        <option value="procesado">{intl.formatMessage({ id: 'bipeosGrid.estado_procesado', defaultMessage: 'Procesado' })}</option>
                        <option value="pendiente">{intl.formatMessage({ id: 'bipeosGrid.estado_pendiente', defaultMessage: 'Pendiente' })}</option>
                      </TextField>
                    </form>
                  </Grid>

                  <Grid
                    xs={12}
                    container
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Grid component={Box} item xs={2} className="align-self-end">
                      <div className="d-flex inline justify-content-around align-items-center">
                        <FormattedMessage id="bipeosGrid.persistir_filtros" defaultMessage="Persistir Filtros" />
                        <Switch
                          onChange={handlePersisteFiltros}
                          checked={persisteFiltros}
                          offColor="#FF4961"
                          onColor="#28D094"
                        />
                      </div>
                    </Grid>
                    <Grid component={Box} item xs={1} className="align-self-end">
                      <button
                        className="btn btn-primary mr-1"
                        onClick={handleSubmit}
                        disabled={loading}
                      >
                        <i className="fa fa-search fa-xs"></i>&nbsp;
                        <FormattedMessage id="bipeosGrid.buscar" defaultMessage="Buscar" />
                      </button>
                    </Grid>
                  </Grid>

                </Grid>
              </Collapse>

              {ultimaRecepcion && (
                <div style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', marginBottom: '4px' }}>
                  <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', fontWeight: 400, color: '#6B6F82' }}>
                    <FormattedMessage id="bipeosGrid.ultima_recepcion" defaultMessage="Última recepción de rutas" />
                    {': ' + ultimaRecepcion}
                  </span>
                </div>
              )}

              <TableContainer>
                <Table aria-label="bipeos table" className="table-hover">
                  <TableHead>
                    <TableRow>
                      <StyledTableCell align="left" onClick={function() { requestSort("dominio"); }} style={{ cursor: "pointer" }}>
                        <FormattedMessage id="bipeosGrid.col_dominio" defaultMessage="Dominio" />
                        <img className="ml-1" src={getSortImg("dominio")} alt="/" style={{ height: "12px", width: "22px" }} />
                      </StyledTableCell>
                      <StyledTableCell align="left" onClick={function() { requestSort("cantidadRutas"); }} style={{ cursor: "pointer" }}>
                        <FormattedMessage id="bipeosGrid.col_cantidad_rutas" defaultMessage="Cantidad de rutas" />
                        <img className="ml-1" src={getSortImg("cantidadRutas")} alt="/" style={{ height: "12px", width: "22px" }} />
                      </StyledTableCell>
                      <StyledTableCell align="left" onClick={function() { requestSort("fechaHoraBipeo"); }} style={{ cursor: "pointer" }}>
                        <FormattedMessage id="bipeosGrid.col_fecha_bipeo" defaultMessage="Fecha y hora del bipeo" />
                        <img className="ml-1" src={getSortImg("fechaHoraBipeo")} alt="/" style={{ height: "12px", width: "22px" }} />
                      </StyledTableCell>
                      <StyledTableCell align="left" onClick={function() { requestSort("fechaRecepcion"); }} style={{ cursor: "pointer" }}>
                        <FormattedMessage id="bipeosGrid.col_fecha_recepcion" defaultMessage="Fecha de recepción del bipeo" />
                        <img className="ml-1" src={getSortImg("fechaRecepcion")} alt="/" style={{ height: "12px", width: "22px" }} />
                      </StyledTableCell>
                      <StyledTableCell align="left" onClick={function() { requestSort("estado"); }} style={{ cursor: "pointer" }}>
                        <FormattedMessage id="bipeosGrid.col_estado" defaultMessage="Estado de procesamiento" />
                        <img className="ml-1" src={getSortImg("estado")} alt="/" style={{ height: "12px", width: "22px" }} />
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <StyledTableCell colSpan={5} align="center" style={{ padding: 24 }}>
                          <CircularProgress />
                        </StyledTableCell>
                      </TableRow>
                    ) : bipeos.length === 0 ? (
                      <TableRow>
                        <StyledTableCell colSpan={5} align="center" style={{ padding: 24 }}>
                          <FormattedMessage id="bipeosGrid.sin_resultados" defaultMessage="No se encontraron resultados para los filtros aplicados." />
                        </StyledTableCell>
                      </TableRow>
                    ) : (
                      bipeos.map(function(b) {
                        return (
                          <TableRow key={b.id} className="tableRow">
                            <StyledTableCell>{b.dominio}</StyledTableCell>
                            <StyledTableCell>
                              {b.cantidadRutas !== null && b.cantidadRutas !== undefined ? b.cantidadRutas : '-'}
                            </StyledTableCell>
                            <StyledTableCell>
                              {b.fechaHoraBipeo ? moment(b.fechaHoraBipeo).format('DD/MM/YYYY HH:mm') : '-'}
                            </StyledTableCell>
                            <StyledTableCell>
                              {b.fechaRecepcion ? moment(b.fechaRecepcion).format('DD/MM/YYYY HH:mm') : '-'}
                            </StyledTableCell>
                            <StyledTableCell>
                              {b.procesado ? (
                                <span className="badge badge-success">
                                  <FormattedMessage id="bipeosGrid.estado_procesado" defaultMessage="Procesado" />
                                </span>
                              ) : (
                                <span className="badge badge-warning">
                                  <FormattedMessage id="bipeosGrid.estado_pendiente" defaultMessage="Pendiente" />
                                </span>
                              )}
                            </StyledTableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={rowsPerPage}
                component="div"
                count={paginationCount}
                rowsPerPage={pagination.perPage}
                page={pagination.page}
                onPageChange={function(e, newPage) { setPagination(function(p) { return Object.assign({}, p, { page: newPage }); }); }}
                onRowsPerPageChange={function(e) { setPagination(function(p) { return Object.assign({}, p, { perPage: parseInt(e.target.value, 10), page: 0 }); }); }}
                labelRowsPerPage="Mostrar"
                labelDisplayedRows={function(ref) { var from = ref.from, to = ref.to, count = ref.count; return "Mostrando " + from + " a " + to + " de " + count; }}
              />

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BipeosGrid;
